/**
 * Manual retry-with-backoff for the narrow case TanStack Query's own
 * built-in retry doesn't cover: a one-off imperative call (e.g. inside
 * a mutation's `onError`, or a non-Query axios call) that should retry
 * transient network/5xx failures but never retry a real business-rule
 * rejection (4xx). Query/mutation-level retry policy itself lives in
 * `query-client.ts` — this is the shared backoff primitive both use.
 */
import { ApiError } from '@app-types/api.types';

export function isRetryableError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  // 0 = network error/timeout (no response reached the server at all) — always worth retrying.
  // 5xx = server-side failure — worth retrying a bounded number of times.
  // Never retry 4xx: a validation/permission/conflict rejection will not change on its own.
  return error.statusCode === 0 || error.statusCode >= 500;
}

export interface RetryOptions {
  maxAttempts?: number;
  baseDelayMs?: number;
}

export async function withRetry<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const { maxAttempts = 3, baseDelayMs = 300 } = options;
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt === maxAttempts || !isRetryableError(error)) {
        throw error;
      }
      const delay = baseDelayMs * 2 ** (attempt - 1);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}
